import React, { useMemo } from 'react'
import useOffers from './useOffers'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { nonNullable, sortBy } from 'lib/array/arrayUtils'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import NumberRating from 'components/Common/NumberRating'
import LEHotelSearchCancellationPolicyCell from 'components/OfferComparison/cells/LEHotelSearchCancellationPolicyCell'
import BedbankHotelSearchCancellationPolicyCell from 'components/OfferComparison/cells/BedbankHotelSearchCancellationPolicyCell'
import CellContent from 'components/OfferComparison/cells/CellContent'
import LEHotelSearchPointsCell from 'components/OfferComparison/cells/LEHotelSearchPointsCell'
import BedbankHotelSearchPointsCell from 'components/OfferComparison/cells/BedbankHotelSearchPointsCell'
import LEHotelSearchInclusionsCell from 'components/OfferComparison/cells/LEHotelSearchInclusionsCell'
import BedbankHotelSearchInclusionsCell from 'components/OfferComparison/cells/BedbankHotelSearchInclusionsCell'
import LEHotelRoomSizeCell from 'components/OfferComparison/cells/LEHotelRoomSizeCell'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import LEHotelExclusiveInclusionsCell from 'components/OfferComparison/cells/LEHotelExclusiveInclusionsCell'
import BedbankHotelExclusiveInclusionsCell from 'components/OfferComparison/cells/BedbankHotelExclusiveInclusionsCell'
import BodyText from 'LuxKit/Typography/BodyText'
import LeHotelCartCancellationPolicyCell from 'components/OfferComparison/cells/LEHotelCartCancellationPolicyCell'
import LEHotelDateCell from 'components/OfferComparison/cells/LEHotelDateCell'

const offerComparisonRowTypes = [
  'pricing',
  'dates',
  'rating',
  'cancellationPolicy',
  'roomSize',
  'points',
  'inclusions',
] as const

type OfferComparisonRowType = typeof offerComparisonRowTypes[number]

interface OfferComparisonRow {
  type: OfferComparisonRowType
  element: React.ReactElement
}

export interface OfferComparisonOption {
  selection: App.OfferComparisonSelection
  offer: App.AnyOffer
  id: string
  name: string
  packageName?: string
  location: string
  images: Array<App.Image>
  rows: { [type in OfferComparisonRowType]?: OfferComparisonRow }
}

interface OfferComparison {
  options: Array<OfferComparisonOption>
  rows: Array<OfferComparisonRowType>
}

type OfferComparisonResult = {
  status: 'loading'
} | {
  status: 'error'
  message: string
} | {
  status: 'success'
  comparison: OfferComparison
}

export default function useOfferComparison(selections: Array<App.OfferComparisonSelection>): OfferComparisonResult {
  const [offers, offersLoading] = useOffers(selections.map(s => s.offerId))

  return useMemo(() => {
    if (offersLoading) {
      return { status: 'loading' }
    } else if (offers.length !== selections.length) {
      return {
        status: 'error',
        message: 'Failed to load offer data',
      }
    } else {
      try {
        const comparison = generateOfferComparison(selections, offers)

        return {
          status: 'success',
          comparison,
        }
      } catch(e) {
        return {
          status: 'error',
          message: e.message,
        }
      }
    }
  }, [offers, offersLoading, selections])
}

function generateOfferComparison(selections: Array<App.OfferComparisonSelection>, offers: Array<App.AnyOffer>): OfferComparison {
  const sortedSelection = sortBy(selections, selection => selection.priceTotals?.price ?? 0, 'asc')
  const comparisonOffers = sortedSelection.map((selection, i) => getComparisonDataForOffer(selection, offers[i], selections))
  const rows = offerComparisonRowTypes.filter(type => comparisonOffers.some(o => o.rows[type]))

  return {
    options: comparisonOffers,
    rows,
  }
}

function getComparisonDataForOffer(selection: App.OfferComparisonSelection, offer: App.AnyOffer, selections: Array<App.OfferComparisonSelection>): OfferComparisonOption {
  if (selection.offerType !== offer.type) {
    throw new Error('Error generating comparison')
  }

  if (isLEHotel(offer)) {
    return getComparisonDataForLEHotelOffer(selection, offer, selections)
  } else if (isBedbankOffer(offer)) {
    return getComparisonDataForBedbankHotelOffer(selection, offer, selections)
  } else {
    throw new Error('Offer type not supported for comparison')
  }
}

function getComparisonDataForLEHotelOffer(selection: App.OfferComparisonSelection, offer: App.HotelOffer, selections: Array<App.OfferComparisonSelection>): OfferComparisonOption {
  const pkg = selection.params?.packageUniqueKey ? offer.packages.find(p => p.uniqueKey === selection.params?.packageUniqueKey) : undefined

  const comparisonOffer: OfferComparisonOption = {
    selection,
    offer,
    id: selection.selectionId,
    name: offer.property.name,
    packageName: pkg?.name,
    location: nonNullable([offer.locationHeading, offer.locationSubheading]).join(', '),
    images: pkg ? pkg.roomType.images : offer.images,
    rows: {},
  }

  const filters = selection.params?.filters

  if (selection.priceTotals) {
    comparisonOffer.rows.pricing = {
      type: 'pricing',
      element: <CellContent>
        <CartReviewPriceStack
          price={selection.priceTotals.price}
          memberPrice={selection.priceTotals.memberPrice}
          value={selection.priceTotals.value}
          luxPlusOfferConfig={offer.luxPlus}
        />
      </CellContent>,
    }
  }

  if (filters) {
    comparisonOffer.rows.dates = {
      type: 'dates',
      element: <LEHotelDateCell filters={filters} />
    }

    comparisonOffer.rows.cancellationPolicy = {
      type: 'cancellationPolicy',
      element: selection.params?.packageUniqueKey ? <LeHotelCartCancellationPolicyCell offer={offer} params={selection.params} /> : <LEHotelSearchCancellationPolicyCell offer={offer} filters={filters} />,
    }

    if (selection.params?.packageUniqueKey) {
      comparisonOffer.rows.roomSize = {
        type: 'roomSize',
        element: <LEHotelRoomSizeCell offer={offer} packageUniqueKey={selection.params.packageUniqueKey} />,
      }
    }

    comparisonOffer.rows.points = {
      type: 'points',
      element: <LEHotelSearchPointsCell offer={offer} filters={filters} packageUniqueKey={selection.params?.packageUniqueKey} />,
    }

    comparisonOffer.rows.inclusions = {
      type: 'inclusions',
      element: selection.params?.packageUniqueKey ?
          <LEHotelExclusiveInclusionsCell offer={offer} filters={filters} packageUniqueKey={selection.params?.packageUniqueKey} selectionId={selection.selectionId} selections={selections} /> :
          <LEHotelSearchInclusionsCell offer={offer} filters={filters} />,
    }
  }

  if (isOfferRatingDisplayable(offer.property.rating)) {
    comparisonOffer.rows.rating = {
      type: 'rating',
      element: <CellContent>
        <NumberRating
          rating={offer.property.rating.score}
          total={offer.property.rating.reviewsTotal}
          variant="medium"
          numberPosition="start"
        />
      </CellContent>,
    }
  }

  return comparisonOffer
}

function getComparisonDataForBedbankHotelOffer(selection: App.OfferComparisonSelection, offer: App.BedbankOffer | App.BedbankOfferSummary, selections: Array<App.OfferComparisonSelection>): OfferComparisonOption {
  const pkg = selection.params?.roomId ? offer.packages.find(p => p.roomId === selection.params?.roomId) : undefined

  const comparisonOffer: OfferComparisonOption = {
    selection,
    offer,
    id: selection.selectionId,
    name: offer.name,
    packageName: pkg?.name,
    location: offer.property.locationTitleOverride || [offer.property.address.city, offer.property.address.countryName].filter(Boolean).join(', '),
    images: pkg ? pkg.images : offer.images,
    rows: {},
  }

  const filters = selection.params?.filters

  if (selection.priceTotals) {
    comparisonOffer.rows.pricing = {
      type: 'pricing',
      element: <CellContent>
        <CartReviewPriceStack
          price={selection.priceTotals.price}
          memberPrice={selection.priceTotals.memberPrice}
          value={selection.priceTotals.value}
          luxPlusOfferConfig={offer.luxPlus}
        />
      </CellContent>,
    }
  }

  if (filters) {
    comparisonOffer.rows.cancellationPolicy = {
      type: 'cancellationPolicy',
      element: <BedbankHotelSearchCancellationPolicyCell offer={offer} filters={filters} />,
    }

    if (pkg) {
      const features = new Map(pkg.features.map((f) => ([f.id, f.text])))
      const roomSize = features.get('dimension')
      if (roomSize) {
        comparisonOffer.rows.roomSize = {
          type: 'roomSize',
          element: <CellContent>
            <BodyText variant="medium">{roomSize}</BodyText>
          </CellContent>,
        }
      }
    }

    comparisonOffer.rows.points = {
      type: 'points',
      element: <BedbankHotelSearchPointsCell offer={offer} filters={filters} priceTotals={selection.priceTotals} />,
    }

    comparisonOffer.rows.inclusions = {
      type: 'inclusions',
      element: filters?.roomRateId ?
          <BedbankHotelExclusiveInclusionsCell
            offer={offer}
            filters={filters}
            selectionId={selection.selectionId}
            selections={selections}
          /> :
          <BedbankHotelSearchInclusionsCell offer={offer} filters={filters} />,
    }
  }

  if (isOfferRatingDisplayable(offer.property.rating)) {
    comparisonOffer.rows.rating = {
      type: 'rating',
      element: <CellContent>
        <NumberRating
          rating={offer.property.rating.score}
          total={offer.property.rating.reviewsTotal}
          variant="medium"
          numberPosition="start"
        />
      </CellContent>,
    }
  }

  return comparisonOffer
}
